import React, { useState } from 'react';
import { WorkoutHistoryManager } from './WorkoutHistoryManager';

/**
 * WorkoutHistoryDemo - Demo component for testing workout history
 * Shows workout history features and filtering capabilities
 */
export const WorkoutHistoryDemo: React.FC = () => {
  const [showDemo, setShowDemo] = useState(false);

  if (showDemo) {
    return (
      <WorkoutHistoryManager onBack={() => setShowDemo(false)} />
    );
  }

  return (
    <div className="workout-history-demo p-4 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2">История тренировок</h1>
        <p className="text-gray-600">
          Демонстрация системы истории тренировок с фильтрацией и детальным просмотром
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Features */}
        <div className="space-y-6">
          <h2 className="text-lg font-semibold">Возможности системы</h2>
          
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium mb-3">📋 Список тренировок</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Хронологическая сортировка</li>
              <li>• Поиск по названию тренировки</li>
              <li>• Фильтрация по дате и длительности</li>
              <li>• Фильтр по наличию комментариев</li>
              <li>• Пагинация для больших списков</li>
            </ul>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium mb-3">🔍 Детальный просмотр</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Полная информация о тренировке</li>
              <li>• Список всех упражнений и подходов</li>
              <li>• Статистика: объём, повторы, время</li>
              <li>• Комментарии к тренировке и упражнениям</li>
              <li>• Возможность удаления тренировки</li>
            </ul>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium mb-3">📊 Статистика</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Общее количество тренировок</li>
              <li>• Средняя длительность тренировок</li>
              <li>• Общий объём нагрузки</li>
              <li>• Популярные упражнения</li>
              <li>• Активность по дням</li>
            </ul>
          </div>
        </div>

        {/* Mock Data Preview */}
        <div className="space-y-6">
          <h2 className="text-lg font-semibold">Тестовые данные</h2>
          
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium mb-3">Примеры тренировок:</h3>
            <div className="space-y-3">
              <div className="border-l-4 border-blue-500 pl-3">
                <div className="font-medium">Тренировка A - Верх тела</div>
                <div className="text-sm text-gray-600">Вчера • 45 мин • С комментариями</div>
              </div>
              
              <div className="border-l-4 border-green-500 pl-3">
                <div className="font-medium">Тренировка B - Низ тела</div>
                <div className="text-sm text-gray-600">3 дня назад • 52 мин</div>
              </div>
              
              <div className="border-l-4 border-purple-500 pl-3">
                <div className="font-medium">Тренировка C - Кардио</div>
                <div className="text-sm text-gray-600">10 дней назад • 30 мин</div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium mb-3">Статистика (mock):</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Всего тренировок:</span>
                <span className="ml-2 font-medium">15</span>
              </div>
              <div>
                <span className="text-gray-600">Средняя длительность:</span>
                <span className="ml-2 font-medium">45 мин</span>
              </div>
              <div>
                <span className="text-gray-600">Общий объём:</span>
                <span className="ml-2 font-medium">23,100 кг</span>
              </div>
              <div>
                <span className="text-gray-600">За эту неделю:</span>
                <span className="ml-2 font-medium">3</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium mb-3">Популярные упражнения:</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Жим лёжа</span>
                <span className="text-gray-600">8 раз</span>
              </div>
              <div className="flex justify-between">
                <span>Приседания</span>
                <span className="text-gray-600">7 раз</span>
              </div>
              <div className="flex justify-between">
                <span>Подтягивания</span>
                <span className="text-gray-600">6 раз</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Button */}
      <div className="mt-8 text-center">
        <button
          onClick={() => setShowDemo(true)}
          className="bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 text-lg"
        >
          Открыть демо истории тренировок
        </button>
      </div>

      {/* Features Info */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">Режим разработки:</h3>
        <p className="text-sm text-blue-700">
          В демо используются тестовые данные. В реальном приложении история будет загружаться из базы данных 
          и показывать ваши фактические тренировки с возможностью фильтрации и поиска.
        </p>
      </div>
    </div>
  );
};

export default WorkoutHistoryDemo;